import { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AddAidRequestFormDialog from '../dialogs/AddAidRequestFormDialog';
import { type AidRequestForm } from '@entities';
import { useOrganization } from '../../contexts/OrganizationProvider';
import { useAlert } from '../../contexts/AlertProvider';
import useQuestion from '../../dialog-components/QuestionDialog';
import { aidRequestFormRepo } from '@repositories';
import './FormsPage.scss';

const FormsPage = () => {
    const { currOrganization, getCurrOrganization } = useOrganization();
    const { setAlertText, setSeverity } = useAlert();
    const question = useQuestion();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedForm, setSelectedForm] = useState<AidRequestForm>();

    const handleOpenDialog = (form: AidRequestForm) => {
        setSelectedForm(form);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedForm(undefined);
    };

    const handleAddForm = () => {
        setSelectedForm(undefined);
        setOpenDialog(true);
    };

    const handleCopyLink = (formId: string) => {
        const formLink = `https://www.we-me.app/user-request/${currOrganization?.id}/${formId}`;
        navigator.clipboard.writeText(formLink);
    };

    const handleDeleteForm = async (form: AidRequestForm) => {
        const confirmed = await question(
            "האם אתה בטוח שברצונך למחוק את הטופס?",
            `הטופס "${form.title}" יימחק לצמיתות ולא ניתן יהיה לשחזר אותו.`
        );

        if (confirmed) {
            try {
                await aidRequestFormRepo.delete({ id: form.id });
                setAlertText("הטופס נמחק בהצלחה");
                setSeverity("success");
                getCurrOrganization(); // Refresh the organization data to update the forms list
            } catch (error) {
                console.error("Error deleting form:", error);
                setAlertText("קרתה תקלה במחיקת הטופס, יש לנסות שוב");
                setSeverity("error");
            }
        }
    };

    const truncateText = (text: string, maxLength: number) => {
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    return (
        <Box className="aid-forms-container" sx={{ padding: '32px', backgroundColor: '#f9f9f9' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 'bold',
                        color: '#333',
                        textTransform: 'uppercase',
                        letterSpacing: '1.5px',
                    }}
                >
                    טפסים
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddForm}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        padding: '12px 24px',
                        borderRadius: '8px',
                    }}
                    startIcon={<AddIcon />}
                >
                    הוספת טופס
                </Button>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '24px',
                }}
            >
                {currOrganization?.forms?.map((form) => (
                    <Card
                        key={form.id}
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: '#ffffff',
                            height: "25vh",
                            justifyContent: 'space-between',
                            borderRadius: '12px',
                            boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                            },
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#333',
                                    marginBottom: '8px'
                                }}
                            >
                                {form.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#666', marginBottom: '16px' }}
                            >
                                {truncateText(form.description, 150)}
                            </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between', padding: '16px' }}>
                            <Box sx={{ display: 'flex', gap: '8px' }}>
                                <Tooltip title="העתקת קישור לטופס">
                                    <IconButton
                                        onClick={() => handleCopyLink(form.id)}
                                        sx={{ color: '#666' }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="מחיקת הטופס">
                                    <IconButton
                                        onClick={() => handleDeleteForm(form)}
                                        sx={{ color: '#d32f2f' }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleOpenDialog(form)}
                                sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                startIcon={<EditIcon />}
                            >
                                עריכה
                            </Button>
                        </CardActions>
                    </Card>
                ))}
            </Box>
            {openDialog && (
                <AddAidRequestFormDialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    initialValues={selectedForm ? {
                        title: selectedForm.title || "",
                        description: selectedForm.description || "",
                        categories: selectedForm?.categories?.map((cat) => cat.name) || [],
                        notes: selectedForm.notes || '',
                        id: selectedForm.id,
                        team: selectedForm.team || undefined,
                    } : undefined}
                />
            )}
        </Box>
    );
};

export default FormsPage;